/*
 * DaisyBee Arts — header drawer
 *
 * Opens the full-screen navigation, keeps focus inside it while open, and
 * hands focus back to the button that opened it on close. The drawer carries
 * `inert` while closed so assistive tech and Tab skip it entirely.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class DrawerController {
  constructor(drawer) {
    this.drawer = drawer;
    this.panel = drawer.querySelector('.db-drawer__panel');
    this.opener = null;
    this.isOpen = false;

    this.onKeydown = this.onKeydown.bind(this);

    for (const button of drawer.querySelectorAll('[data-db-drawer-close]')) {
      button.addEventListener('click', () => this.close());
    }
  }

  open(opener) {
    if (this.isOpen) return;

    this.opener = opener ?? null;
    this.isOpen = true;

    this.drawer.removeAttribute('inert');
    this.drawer.classList.add('is-open');
    this.opener?.setAttribute('aria-expanded', 'true');

    // Hold the page still behind the drawer without losing scroll position.
    this.scrollY = window.scrollY;
    document.body.style.setProperty('overflow', 'hidden');

    document.addEventListener('keydown', this.onKeydown);

    // Focus the close button once the panel has started moving, so the browser
    // does not fight the transition by scrolling to it.
    requestAnimationFrame(() => {
      this.drawer.querySelector('.db-drawer__close')?.focus();
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.opener?.setAttribute('aria-expanded', 'false');

    document.body.style.removeProperty('overflow');
    document.removeEventListener('keydown', this.onKeydown);

    // Move focus out before marking inert, or the browser drops it to <body>.
    this.opener?.focus();
    this.opener = null;

    const finish = () => this.drawer.setAttribute('inert', '');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      finish();
    } else {
      this.panel?.addEventListener('transitionend', finish, { once: true });
      // Belt and braces if the transition never fires (display changes, etc.).
      setTimeout(finish, 700);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null
    );
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

function init(root = document) {
  for (const drawer of root.querySelectorAll('[data-db-drawer]')) {
    if (drawer.dataset.dbDrawerBound === 'true') continue;
    drawer.dataset.dbDrawerBound = 'true';

    const controller = new DrawerController(drawer);
    drawer.dbController = controller;

    const openers = document.querySelectorAll(`[data-db-drawer-open][aria-controls="${drawer.id}"]`);
    for (const opener of openers) {
      opener.addEventListener('click', () => controller.open(opener));
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => init(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  for (const drawer of event.target.querySelectorAll('[data-db-drawer]')) {
    drawer.dbController?.close();
  }
});
